import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PreferenceProvider } from './contexts/PreferenceContext';
import { MeetingProvider } from './contexts/MeetingContext';
import { AdminProvider } from './contexts/adminContext';

import Dashboard from './pages/Dashboard';
import CreateMeeting from './pages/CreateMeeting';
import Calendar from './pages/Calendar';
import MeetingNew from './pages/MeetingNew';
import PreMeetingRoom from './pages/PreMeetingRoom';
import WaitingRoom from './pages/WaitingRoom';
import Settings from './pages/Settings';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Community from './pages/Community';
import Notes from './pages/Notes';
import Analytics from './pages/Analytics';
import HelpSupport from './pages/HelpSupport';
import AdminPortal from './pages/AdminPortal';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import SsoCallback from './pages/SsoCallback';

import { jwtDecode } from 'jwt-decode';
import { collection, onSnapshot } from "firebase/firestore";
import { db, generateToken } from './utils/firebase';

import NotificationPopup from './contexts/NotificationPopup';
import Register from './pages/Register';

// --------------------------------------------------
// TYPES
// --------------------------------------------------
interface JwtPayload {
  exp: number;
  [key: string]: any;
}

// --------------------------------------------------
// PAGE TITLES (CENTRALIZED)
// --------------------------------------------------
const PAGE_TITLES: Record<string, string> = {
  "/": "Dashboard",
  "/create-meeting": "Create Meeting",
  "/calendar": "Calendar",
  "/meeting-new": "Meeting",
  "/pre-meeting": "Pre Meeting",
  "/waiting-room": "Waiting Room",
  "/notes": "Notes",
  "/analytics": "Analytics",
  "/settings": "Settings",
  "/community": "Community",
  "/admin": "Admin Portal",
  "/help": "Help & Support",
  "/login": "Login",
  "/forgot-password": "Forgot Password",
};

// --------------------------------------------------
// ROUTE GUARDS
// --------------------------------------------------
const PrivateRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { token, logout } = useAuth();

  if (!token) return <Navigate to="/login" replace />;

  try {
    const decoded = jwtDecode<JwtPayload>(token);
    const now = Date.now() / 1000;

    if (decoded.exp < now) {
      logout?.();
      return <Navigate to="/login" replace />;
    }
  } catch {
    logout?.();
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PublicRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { token } = useAuth();
  if (token) return <Navigate to="/" replace />;
  return children;
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

// --------------------------------------------------
// APP ROUTES
// --------------------------------------------------
const AppRoutes: React.FC = () => {
  const { user, token } = useAuth();
  const location = useLocation();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // CENTRALIZED TITLE HANDLER
  useEffect(() => {
    const baseTitle = "VELA Conferencing";

    // Handle dynamic routes
    if (location.pathname.startsWith("/accept-invite")) {
      document.title = `Accept Invite | ${baseTitle}`;
      return;
    }

    const title =
      PAGE_TITLES[location.pathname] || "Dashboard";

    // document.title = `${title} | ${baseTitle}`;
    document.title = `${baseTitle} | ${title}`;
  }, [location.pathname]);

  //  FCM TOKEN SETUP
  const saveFcmToken = async (fcmToken: string) => {
    try {
      await fetch(`${backendUrl}/api/auth/update-fcm-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ fcmToken })
      });
    } catch (error) {
      console.error("Error saving FCM token:", error);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (isIOS()) return;

    const initFCM = async () => {
      try {
        const fcmToken = await generateToken();
        if (fcmToken) {
          await saveFcmToken(fcmToken);
        }
      } catch (err) {
        console.error("FCM init failed:", err);
      }
    };

    initFCM();
  }, [user]);

  return (
    <Routes>
      {/* PROTECTED */}
      <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/create-meeting" element={<PrivateRoute><CreateMeeting /></PrivateRoute>} />
      <Route path="/calendar" element={<PrivateRoute><Calendar /></PrivateRoute>} />
      <Route path="/meeting-new" element={<PrivateRoute><MeetingNew /></PrivateRoute>} />
      <Route path="/pre-meeting" element={<PrivateRoute><PreMeetingRoom /></PrivateRoute>} />
      <Route path="/waiting-room" element={<PrivateRoute><WaitingRoom /></PrivateRoute>} />
      <Route path="/notes" element={<PrivateRoute><Notes /></PrivateRoute>} />
      <Route path="/analytics" element={<PrivateRoute><Analytics /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/community" element={<PrivateRoute><Community /></PrivateRoute>} />
      <Route path="/help" element={<PrivateRoute><HelpSupport /></PrivateRoute>} />

      {/* ADMIN */}
      {user?.isAdmin && (
        <Route path="/admin" element={<PrivateRoute><AdminPortal /></PrivateRoute>} />
      )}

      {/* PUBLIC */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} /> 
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      <Route path="/sso-callback" element={<SsoCallback />} />

      {/* REDIRECT */}
      <Route path="/meeting/:roomId" element={<Navigate to="/meeting-new" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// --------------------------------------------------
// MAIN APP
// --------------------------------------------------
const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <PreferenceProvider>
          <MeetingProvider>
            <AdminProvider>
              <NotificationPopup />
              <ThemeProvider>
                <AppRoutes />
              </ThemeProvider>
            </AdminProvider>
          </MeetingProvider>
        </PreferenceProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
